<script lang="ts" setup>
  import type { ICloseArgs } from '#shared/types/modal.types';
  import type { TComponentColor } from '#shared/types/ui.types';

  const props = defineProps<{
    title: string;
    text?: string;
    actions?: GenericModalAction[];
    noOverlayClose?: boolean;
    noCloseButton?: boolean;
    maxWidth?: string;
  }>();

  const emit = defineEmits<{ close: [ICloseArgs] }>();

  const DEFAULT_MAX_WIDTH = '520px';

  export interface GenericModalAction {
    label: string;
    color?: TComponentColor;
    variant?: 'solid' | 'outline' | 'soft' | 'ghost' | 'link';
    class?: string;
    onClick?: () => void;
  }

  const maxWidth = computed(() => props.maxWidth || DEFAULT_MAX_WIDTH);

  function performAction(action = () => {}) {
    if (typeof action === 'function') action();
    emit('close', {});
  }

  function close() {
    emit('close', {});
  }
</script>

<template>
  <UModal
    :title="title"
    :description="text || ''"
    :close="{ onClick: close }"
    :ui="{ content: `overflow-y-hidden w-full max-w-[${maxWidth}]` }"
    :dismissible="!noOverlayClose"
  >
    <template #content>
      <div class="overflow-y-scroll bg-white flex flex-col gap-6 font-barlow text-[#101317] text-center p-10">
        <UButton
          icon="mdi-close"
          variant="outline"
          class="size-5 absolute top-2.5 right-2.5 cursor-pointer"
          :ui="{
            base: 'flex justify-center p-0 md:p-0 rounded-full bg-transparent ring ring-[#101317] bg-transparent hover:bg-transparent active:bg-transparent',
            leadingIcon: 'size-4 text-[#101317]',
          }"
          @click="close"
        />

        <h2 class="text-3xl font-semibold leading-none">{{ title }}</h2>

        <div v-if="text" class="flex items-center justify-center gap-2.5 font-medium text-lg" v-html="text" />

        <div v-if="actions?.length" class="flex items-center gap-2.5">
          <CoreButton
            v-for="action in actions"
            :key="action.label"
            class="basis-full w-full h-[44px]"
            :class="action.class || ''"
            :color="action.color"
            :variant="action.variant"
            @click="performAction(action.onClick)"
          >
            <span class="font-barlow text-base font-medium">{{ action.label }}</span>
          </CoreButton>
        </div>
      </div>
    </template>
  </UModal>
</template>
